import React, { useState, useEffect } from 'react';

const AnalyticsDashboard = ({ onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState('');
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  // Get API URL from environment - for analytics, use local API
  const API = process.env.REACT_APP_BACKEND_URL ? 
    `${process.env.REACT_APP_BACKEND_URL}/api` : 
    'http://localhost:8001/api';

  // Password protection
  const DASHBOARD_PASSWORD = 'gibson01';

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === DASHBOARD_PASSWORD) {
      setIsAuthenticated(true);
      setAuthError('');
      // Store auth in sessionStorage for this session
      sessionStorage.setItem('analytics_auth', 'authenticated');
    } else {
      setAuthError('Falsches Passwort! Bitte versuchen Sie es erneut.');
      setPasswordInput('');
    }
  };

  useEffect(() => {
    // Check if already authenticated in this session
    const sessionAuth = sessionStorage.getItem('analytics_auth');
    if (sessionAuth === 'authenticated') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchAnalytics();
      // Refresh every 30 seconds
      const interval = setInterval(fetchAnalytics, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const fetchAnalytics = async () => {
    try {
      const response = await fetch(`${API}/analytics/stats`);
      const data = await response.json();
      setStats(data);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Analytics-Daten');
      console.error('Analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleResetAnalytics = async () => {
    setIsResetting(true);
    try {
      const response = await fetch(`${API}/analytics/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password: DASHBOARD_PASSWORD })
      });

      if (!response.ok) {
        throw new Error('Failed to reset analytics');
      }

      const data = await response.json();
      
      // Show success message
      alert(`✅ Analytics erfolgreich zurückgesetzt!\n${data.deleted_count} Events gelöscht.`);
      
      // Refresh analytics
      await fetchAnalytics();
      
      // Close confirmation dialog
      setShowResetConfirm(false);
    } catch (err) {
      console.error('Reset error:', err);
      alert('❌ Fehler beim Zurücksetzen der Analytics');
    } finally {
      setIsResetting(false);
    }
  };

  // Show password login if not authenticated
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center relative">
        {/* Close button */}
        {onClose && (
          <button 
            onClick={onClose}
            className="absolute top-6 right-6 w-12 h-12 bg-red-500/20 hover:bg-red-500/40 rounded-full flex items-center justify-center text-red-400 hover:text-red-300 text-xl font-bold transition-all hover:scale-110 z-10"
            title="Dashboard schließen"
          >
            ✕
          </button>
        )}

        <div className="bg-white rounded-2xl p-8 md:p-12 border border-gray-200 shadow-2xl max-w-md w-full mx-4">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#ca91cf]/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-2xl">🔐</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
              Analytics Dashboard
            </h2>
            <p className="text-gray-600">Passwort erforderlich</p>
          </div>

          <form onSubmit={handlePasswordSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Passwort eingeben:
              </label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-900 placeholder-gray-500 focus:border-[#ca91cf] focus:outline-none focus:ring-2 focus:ring-[#ca91cf]/20 transition-all"
                placeholder="Dashboard-Passwort"
                required
                autoFocus
              />
            </div>

            {authError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                <p className="text-red-400 text-sm">🚫 {authError}</p>
              </div>
            )}

            <button
              type="submit"
              className="w-full bg-gradient-to-r from-[#ca91cf] to-purple-600 hover:from-purple-600 hover:to-[#ca91cf] text-white py-3 px-6 rounded-xl font-semibold shadow-lg transition-all transform hover:scale-105"
            >
              🔓 Dashboard entsperren
            </button>
          </form>

          <div className="mt-8 text-center">
            <p className="text-xs text-gray-500">
              🔒 Sicherer Zugang zu Business Analytics
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#ca91cf] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-900">Analytics laden...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center text-red-400">
          <p className="text-xl mb-4">⚠️ {error}</p>
          <button 
            onClick={fetchAnalytics}
            className="bg-[#ca91cf] hover:bg-purple-600 text-white px-6 py-2 rounded-lg transition-all"
          >
            🔄 Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header with Reset Button */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
          <div className="text-center md:text-left">
            <h1 className="text-4xl font-bold bg-gradient-to-r from-[#ca91cf] to-purple-400 bg-clip-text text-transparent mb-2">
              📊 Analytics Dashboard
            </h1>
            <p className="text-gray-600">Body Transformation - Live Statistiken</p>
          </div>
          
          {/* Reset Button */}
          <div className="flex gap-3 justify-center md:justify-end">
            <button
              onClick={() => setShowResetConfirm(true)}
              className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all flex items-center gap-2"
              title="Analytics zurücksetzen"
            >
              🗑️ Reset Analytics
            </button>
            {onClose && (
              <button 
                onClick={onClose}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all"
                title="Dashboard schließen"
              >
                ✕ Schließen
              </button>
            )}
          </div>
        </div>

        {/* Reset Confirmation Dialog */}
        {showResetConfirm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">⚠️</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Analytics zurücksetzen?</h3>
                <p className="text-gray-600">
                  Alle Analytics-Daten werden unwiderruflich gelöscht. Diese Aktion kann nicht rückgängig gemacht werden.
                </p>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResetConfirm(false)}
                  disabled={isResetting}
                  className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleResetAnalytics}
                  disabled={isResetting}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isResetting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Wird gelöscht...
                    </>
                  ) : (
                    <>🗑️ Ja, löschen</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Content */}
        <div className="text-center mb-8">
          <div className="mt-4 text-sm text-gray-500 flex items-center justify-center gap-4">
            <span>Letztes Update: {new Date().toLocaleTimeString('de-DE')}</span>
            <button 
              onClick={fetchAnalytics} 
              className="text-[#ca91cf] hover:text-purple-600 transition-colors"
            >
              🔄 Aktualisieren
            </button>
            <span>•</span>
            <button 
              onClick={() => {
                sessionStorage.removeItem('analytics_auth');
                setIsAuthenticated(false);
                setPasswordInput('');
              }}
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              🔒 Abmelden
            </button>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
          <div className="bg-white rounded-xl p-5 border border-blue-200 shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-lg">📱</span>
              </div>
              <h3 className="text-sm font-semibold text-blue-700">App Starts</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total_app_starts}</p>
            <p className="text-xs text-blue-600 mt-1">Seitenaufrufe</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-cyan-200 shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center">
                <span className="text-lg">👥</span>
              </div>
              <h3 className="text-sm font-semibold text-cyan-700">Unique Visitors</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.unique_visitors}</p>
            <p className="text-xs text-cyan-600 mt-1">Einzigartige Besucher</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-purple-200 shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-lg">🚀</span>
              </div>
              <h3 className="text-sm font-semibold text-[#ca91cf]">Transformationen</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total_transformations}</p>
            <p className="text-xs text-purple-600 mt-1">Gesamt durchgeführt</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-green-200 shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-lg">📝</span>
              </div>
              <h3 className="text-sm font-semibold text-green-700">Leads</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.total_leads}</p>
            <p className="text-xs text-green-600 mt-1">Kontaktdaten erfasst</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-yellow-200 shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                <span className="text-lg">🎯</span>
              </div>
              <h3 className="text-sm font-semibold text-yellow-700">Engagement</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.app_to_transformation_rate}%</p>
            <p className="text-xs text-yellow-600 mt-1">App → Transformation</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-purple-200 shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-lg">📈</span>
              </div>
              <h3 className="text-sm font-semibold text-purple-700">Lead Rate</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">{stats.conversion_rate}%</p>
            <p className="text-xs text-purple-600 mt-1">Transformation → Lead</p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-orange-200 shadow-lg">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                <span className="text-lg">📅</span>
              </div>
              <h3 className="text-sm font-semibold text-orange-700">Heute</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">
              {stats.daily_stats.find(d => d.date === new Date().toISOString().split('T')[0])?.app_starts || 0}
            </p>
            <p className="text-xs text-orange-600 mt-1">App-Starts heute</p>
          </div>
        </div>

        {/* Geographic Map */}
        <div className="mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-[#ca91cf]">🗺️</span> Besucher aus Basel-Landschaft
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Interactive Topographic Map */}
              <div className="lg:col-span-2">
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200 min-h-[400px] relative overflow-hidden">
                  <svg viewBox="0 0 500 350" className="w-full h-full">
                    {/* Topographic Background Pattern */}
                    <defs>
                      <pattern id="topoPattern" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
                        <circle cx="25" cy="25" r="1" fill="rgba(202, 145, 207, 0.1)"/>
                        <path d="M0,25 Q25,15 50,25 M25,0 Q35,25 25,50" stroke="rgba(202, 145, 207, 0.05)" strokeWidth="0.5" fill="none"/>
                      </pattern>
                      
                      <linearGradient id="elevationGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="rgba(139, 69, 19, 0.3)"/>
                        <stop offset="50%" stopColor="rgba(34, 139, 34, 0.2)"/>
                        <stop offset="100%" stopColor="rgba(72, 61, 139, 0.1)"/>
                      </linearGradient>
                    </defs>
                    
                    {/* Background topographic pattern */}
                    <rect width="500" height="350" fill="url(#topoPattern)"/>
                    
                    {/* Basel-Landschaft Canton Outline (based on real geography) */}
                    <path
                      d="M80 80 L160 70 L220 75 L280 85 L350 95 L420 120 L450 160 L440 200 L400 240 L350 270 L300 285 L250 290 L200 285 L150 275 L100 250 L70 220 L60 180 L65 140 L80 80 Z"
                      fill="url(#elevationGradient)"
                      stroke="rgba(202, 145, 207, 0.8)"
                      strokeWidth="3"
                      className="transition-all duration-500 hover:fill-[#ca91cf]/20 drop-shadow-lg"
                    />
                    
                    {/* Elevation contours for topographic effect */}
                    <g stroke="rgba(202, 145, 207, 0.3)" strokeWidth="1" fill="none">
                      <ellipse cx="200" cy="150" rx="80" ry="40" />
                      <ellipse cx="250" cy="180" rx="60" ry="30" />
                      <ellipse cx="150" cy="200" rx="70" ry="35" />
                      <ellipse cx="320" cy="140" rx="50" ry="25" />
                    </g>
                    
                    {/* Major municipalities with real coordinates (scaled to SVG) */}
                    <g className="text-xs">
                      {/* Liestal (Capital) - 47.485, 7.734 */}
                      <circle cx="280" cy="160" r="6" fill="#ca91cf" stroke="#fff" strokeWidth="2" className="drop-shadow-md"/>
                      <text x="290" y="155" className="text-sm font-bold fill-gray-800 drop-shadow-sm">Liestal</text>
                      <text x="290" y="168" className="text-xs fill-gray-600">(Hauptstadt)</text>
                      
                      {/* Pratteln - 47.521, 7.694 */}
                      <circle cx="250" cy="140" r="4" fill="#ca91cf" stroke="#333" strokeWidth="1"/>
                      <text x="258" y="145" className="text-xs fill-gray-700">Pratteln</text>
                      
                      {/* Muttenz - 47.523, 7.645 */}
                      <circle cx="220" cy="135" r="4" fill="#ca91cf" stroke="#333" strokeWidth="1"/>
                      <text x="228" y="140" className="text-xs fill-gray-700">Muttenz</text>
                      
                      {/* Reinach - 47.497, 7.592 */}
                      <circle cx="180" cy="155" r="4" fill="#ca91cf" stroke="#333" strokeWidth="1"/>
                      <text x="188" y="160" className="text-xs fill-gray-700">Reinach</text>
                      
                      {/* Allschwil - 47.551, 7.536 */}
                      <circle cx="140" cy="120" r="4" fill="#ca91cf" stroke="#333" strokeWidth="1"/>
                      <text x="148" y="125" className="text-xs fill-gray-700">Allschwil</text>
                      
                      {/* Birsfelden - 47.553, 7.623 */}
                      <circle cx="200" cy="115" r="4" fill="#ca91cf" stroke="#333" strokeWidth="1"/>
                      <text x="208" y="120" className="text-xs fill-gray-700">Birsfelden</text>
                    </g>
                    
                    {/* Visitor Circles with Numbers */}
                    {stats.location_stats && stats.location_stats.map((location, index) => {
                      // Basel-Landschaft cities with accurate map positions
                      const cityPositions = {
                        // Main cities
                        'Basel': { x: 160, y: 100 },
                        'Liestal': { x: 280, y: 160 },
                        'Pratteln': { x: 250, y: 140 },
                        'Muttenz': { x: 220, y: 135 },
                        'Reinach': { x: 180, y: 155 },
                        'Allschwil': { x: 140, y: 120 },
                        'Birsfelden': { x: 200, y: 115 },
                        'Binningen': { x: 165, y: 130 },
                        'Oberwil': { x: 170, y: 170 },
                        'Münchenstein': { x: 185, y: 125 },
                        'Aesch': { x: 155, y: 180 },
                        'Gelterkinden': { x: 320, y: 180 },
                        'Laufen': { x: 100, y: 140 },
                        'Sissach': { x: 260, y: 185 },
                        'Arlesheim': { x: 175, y: 145 },
                        'Bottmingen': { x: 170, y: 140 },
                        'Therwil': { x: 160, y: 165 }
                      };
                      
                      const position = cityPositions[location.city] || { 
                        x: 150 + (index * 40), 
                        y: 180 + (index * 20) 
                      };
                      
                      const radius = Math.max(12, Math.min(30, location.count * 3));
                      
                      return (
                        <g key={index}>
                          {/* Visitor circle with glow effect */}
                          <circle 
                            cx={position.x} 
                            cy={position.y} 
                            r={radius + 2}
                            fill="rgba(34, 197, 94, 0.2)" 
                            className="animate-pulse"
                          />
                          <circle 
                            cx={position.x} 
                            cy={position.y} 
                            r={radius}
                            fill="#22c55e" 
                            stroke="#fff" 
                            strokeWidth="2"
                            className="drop-shadow-lg cursor-pointer transition-transform hover:scale-110"
                          />
                          {/* Visitor count number inside circle */}
                          <text 
                            x={position.x} 
                            y={position.y + 1} 
                            className="text-sm font-bold fill-white text-center" 
                            textAnchor="middle" 
                            dominantBaseline="central"
                          >
                            {location.count}
                          </text>
                          {/* City name below circle */}
                          <text 
                            x={position.x} 
                            y={position.y + radius + 15} 
                            className="text-xs fill-gray-700 font-medium text-center" 
                            textAnchor="middle"
                          >
                            {location.city}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                  
                  <div className="absolute bottom-4 left-4 bg-white/90 rounded-lg px-4 py-2 border border-gray-300 shadow-sm">
                    <p className="text-xs text-gray-700 font-medium mb-1">
                      🗺️ Topografische Karte Basel-Landschaft
                    </p>
                    <p className="text-xs text-gray-600">
                      🟢 Besucher-Anzahl • 🟣 Gemeinden
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Kreisgröße = Anzahl Besucher
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Location Statistics */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">📍 Herkunft der Besucher</h3>
                {stats.location_stats && stats.location_stats.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {stats.location_stats.map((location, index) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-medium text-gray-900 text-sm">
                            {location.city}, {location.country}
                          </span>
                          <span className="text-[#ca91cf] font-bold">{location.count}</span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-green-500 to-[#ca91cf] rounded-full transition-all duration-1000"
                            style={{width: `${(location.count / Math.max(...stats.location_stats.map(l => l.count), 1)) * 100}%`}}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl">🌍</span>
                    </div>
                    <p className="text-gray-600">Noch keine Standortdaten verfügbar</p>
                    <p className="text-sm text-gray-500 mt-2">Daten werden bei den nächsten Besuchen erfasst</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Device & Treatment Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-[#ca91cf]">💪</span> Beliebte Behandlungen
            </h2>
            {Object.keys(stats.popular_treatments).length > 0 ? (
              <div className="space-y-3">
                {Object.entries(stats.popular_treatments)
                  .sort(([,a], [,b]) => b - a)
                  .map(([treatment, count]) => (
                    <div key={treatment} className="flex items-center justify-between">
                      <span className="text-gray-700">{treatment}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-[#ca91cf] to-purple-500 rounded-full transition-all duration-1000"
                            style={{width: `${(count / Math.max(...Object.values(stats.popular_treatments))) * 100}%`}}
                          ></div>
                        </div>
                        <span className="text-[#ca91cf] font-bold w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))
                }
              </div>
            ) : (
              <p className="text-gray-500 italic">Noch keine Daten verfügbar</p>
            )}
          </div>

          {/* Device Statistics */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-[#ca91cf]">📱</span> Geräte-Nutzung
            </h2>
            <div className="space-y-4">
              {Object.entries(stats.device_stats).map(([device, count]) => (
                <div key={device} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-xl">
                      {device === 'Mobile' ? '📱' : '💻'}
                    </span>
                    <span className="text-gray-700">{device}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-1000 ${
                          device === 'Mobile' ? 'bg-green-500' : 'bg-blue-500'
                        }`}
                        style={{width: `${(count / Math.max(...Object.values(stats.device_stats), 1)) * 100}%`}}
                      ></div>
                    </div>
                    <span className="text-[#ca91cf] font-bold w-6 text-right">{count}</span>
                    <span className="text-gray-500 text-xs">
                      ({((count / stats.total_app_starts) * 100).toFixed(1)}%)
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Daily Stats Chart */}
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <span className="text-[#ca91cf]">📈</span> 7-Tage Übersicht
            </h2>
            {stats.daily_stats.length > 0 ? (
              <div className="space-y-3">
                {stats.daily_stats.map((day) => (
                  <div key={day.date} className="border-b border-gray-200 pb-3 last:border-b-0">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-700 font-medium">
                        {new Date(day.date).toLocaleDateString('de-DE', {day: '2-digit', month: '2-digit'})}
                      </span>
                      <div className="text-sm text-gray-600">
                        {day.app_starts || 0} Starts • {day.transformations} Trans. • {day.leads} Leads
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all duration-1000"
                          style={{width: `${((day.app_starts || 0) / Math.max(...stats.daily_stats.map(d => d.app_starts || 0), 1)) * 100}%`}}
                        ></div>
                      </div>
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[#ca91cf] rounded-full transition-all duration-1000"
                          style={{width: `${(day.transformations / Math.max(...stats.daily_stats.map(d => d.transformations), 1)) * 100}%`}}
                        ></div>
                      </div>
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500 rounded-full transition-all duration-1000"
                          style={{width: `${(day.leads / Math.max(...stats.daily_stats.map(d => d.leads), 1)) * 100}%`}}
                        ></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 italic">Noch keine Daten verfügbar</p>
            )}
          </div>
        </div>

        {/* Legend */}
        <div className="text-center text-sm text-gray-600">
          <p className="mb-2">🔄 Dashboard aktualisiert sich automatisch alle 30 Sekunden</p>
          <p>Legende: <span className="text-blue-500">■</span> App-Starts • <span className="text-[#ca91cf]">■</span> Transformationen • <span className="text-green-500">■</span> Leads</p>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;